import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import MaterialIconButton from '@mui/material/IconButton'; // Note that this implicitly renames the import from IconButton to MaterialIconButton to avoid naming conflicts below. The MUI component is NOT named MaterialIconButton.
import { useState } from 'react';

import { Button } from 'Components/Buttons/Buttons';
import { Modal } from 'Components/Modal/Modal';
import { Text } from 'Components/Text/Text';
import { ICON_CLOSE, ICON_MOVE_OR_SCROLL_DOWN, ICON_MOVE_OR_SCROLL_UP, ICON_POSITIVE } from 'Config/Icons';
import { ellipt } from 'Helpers/StringUtils';

import styles from './ReorderModal.module.css';

export interface ReorderModalProps<T> {
    /**
     * The title of the modal. For example, "Reorder Sections".
     */
    title: string;
    /**
     * The items to be reordered by the user.
     */
    items: T[];
    /**
     * Must return a unique ID for the given item. Used when mapping over the list of items to render text for them.
     */
    getItemId: (item: T) => string;
    /**
     * The description for a given item. For example, the name of a section.
     */
    getItemDescription: (item: T) => string;
    /**
     * Called with the reordered items when the user clicks the "Confirm" button.
     */
    onOrderSelected: (items: T[]) => void;
    /**
     * Called when the user clicks the "Close" button. Any unsaved changes will be lost.
     */
    onClose: () => void;
}

/**
 * A modal intended for reuse among configuration pages that allow the user to reorder items, e.g., when reordering sections for the IRQ configuration.
 * Displays text for each item, and allows the user to click "up" and "down" arrows to reorder the items.
 */
export const ReorderModal = <T,>(props: ReorderModalProps<T>) => {
    const [reorderedItems, setReorderedItems] = useState<T[]>(props.items);

    const moveUp = (index: number) => {
        const newItems = [...reorderedItems];
        [newItems[index - 1], newItems[index]] = [newItems[index], newItems[index - 1]];
        setReorderedItems(newItems);
    };

    const moveDown = (index: number) => {
        const newItems = [...reorderedItems];
        [newItems[index + 1], newItems[index]] = [newItems[index], newItems[index + 1]];
        setReorderedItems(newItems);
    };

    return (
        <Modal open header={props.title}>
            <div className={styles.itemsToBeReorderedContainer}>
                {reorderedItems.map((item, index) => {
                    const itemId = props.getItemId(item);
                    const itemDescription = props.getItemDescription(item);

                    return (
                        <div key={itemId} className={styles.itemToBeReordered}>
                            <MaterialIconButton aria-label={`Move up ${itemDescription}`} title={`Move up ${itemDescription}`} disabled={index === 0} onClick={() => moveUp(index)}>
                                <FontAwesomeIcon icon={ICON_MOVE_OR_SCROLL_UP} />
                            </MaterialIconButton>
                            <MaterialIconButton aria-label={`Move down ${itemDescription}`} title={`Move down ${itemDescription}`} disabled={index === reorderedItems.length - 1} onClick={() => moveDown(index)}>
                                <FontAwesomeIcon icon={ICON_MOVE_OR_SCROLL_DOWN} />
                            </MaterialIconButton>
                            <div className={styles.itemToBeReorderedText}>
                                <Text variant="Text2" key={props.getItemId(item)} noStyles>
                                    {ellipt(itemDescription)}
                                </Text>
                            </div>
                        </div>
                    );
                })}
            </div>
            <div className={'modalFormButtonContainer'}>
                <Button variant="secondary" onClick={props.onClose} fontAwesomeImage={ICON_CLOSE}>
                    Close
                </Button>
                <Button
                    variant="primary"
                    onClick={() => {
                        props.onOrderSelected(reorderedItems);
                        props.onClose();
                    }}
                    fontAwesomeImage={ICON_POSITIVE}
                >
                    Confirm
                </Button>
            </div>
        </Modal>
    );
};
